import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from 'react-dom';
import "./MissingInfoModal.css";
import TemplateDataContext from "../../../Contexts/TemplateData.context";
import FormDataContext from "../../../Contexts/FormData.context";
import { jsonKeyMapping } from "../../../Config/Constants";
import { isStepCompleted } from "../../../Util/StepCompletionHelper";
import FormBuilder from "../../FormBuilder/FormBuilder";
import { TemplateObject } from "../../../Models/TemplateObject";

interface MissingInfoModalProps {
  open: boolean;
  onClose: () => void;
  initialStepSlug?: string; // optional: start modal focused on a specific missing step
}

const MissingInfoModal: React.FC<MissingInfoModalProps> = ({
  open,
  onClose,
  initialStepSlug,
}) => {
  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const { state: templateState } = useContext(TemplateDataContext);
  const { state: formState } = useContext(FormDataContext);
  const formData = formState?.formData || {};

  const isFormElementCompleted = (
    formElement: TemplateObject,
    formDataObj: TemplateObject
  ): boolean => {
    const fieldId =
      formElement[jsonKeyMapping.FORM_ID] ||
      formElement[jsonKeyMapping.FORM_LABEL] ||
      "";
    if (!fieldId) return true;
    const id = String(fieldId).replaceAll(" ", "");
    const fieldValue = formDataObj[id];
    if (fieldValue === undefined || fieldValue === null || fieldValue === "")
      return false;
    if (Array.isArray(fieldValue) && fieldValue.length === 0) return false;

    const dynamicRegex = new RegExp(`^${id}\\d+$`);
    const dynamicFields = Object.keys(formDataObj).filter((key) =>
      dynamicRegex.test(key)
    );
    if (
      formElement[jsonKeyMapping.FORM_ADD_MORE] === "yes" ||
      dynamicFields.length > 0
    ) {
      for (const dynamicKey of dynamicFields) {
        const dynamicValue = formDataObj[dynamicKey];
        if (
          dynamicValue === undefined ||
          dynamicValue === null ||
          dynamicValue === ""
        )
          return false;
      }
    }

    if (formElement[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS]) {
      for (const nestedElement of formElement[
        jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS
      ]) {
        if (!isFormElementCompleted(nestedElement, formDataObj)) return false;
      }
    }

    if (formElement.inputRadio && Array.isArray(formElement.inputRadio)) {
      for (const radioOption of formElement.inputRadio) {
        const isSelected = fieldValue === radioOption.value;
        if (
          isSelected &&
          radioOption[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS]
        ) {
          for (const moreField of radioOption[
            jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS
          ]) {
            if (!isFormElementCompleted(moreField, formDataObj)) return false;
          }
        }
      }
    }

    if (formElement[jsonKeyMapping.FORM_ADD_MORE_GROUP]) {
      for (const groupElement of formElement[
        jsonKeyMapping.FORM_ADD_MORE_GROUP
      ]) {
        if (!isFormElementCompleted(groupElement, formDataObj)) return false;
      }
    }

    return true;
  };

  const activeSteps: TemplateObject[] = templateState?.activeTemplateSteps || [];
  // Exclude the dynamically injected Review & Submit summary step from the
  // missing info walkthrough. That step (slugify = 'review-submit') has no
  // input fields and is intended only as a final read-only review screen.
  // Without this filter the modal would incorrectly treat it as the last
  // "missing" step (because it is never marked complete in isStepCompleted).
  const stepsWithMissing = useMemo(() => {
    return (activeSteps || [])
      // filter out review summary step
      .filter((s) => s[jsonKeyMapping.SLUGIFY] !== "review-submit")
      // retain only steps that actually have missing fields
      .filter((s) => !isStepCompleted(s, formData));
  }, [activeSteps, formData]);

  const computeMissingFieldsForStep = (step: TemplateObject) => {
    const group = step?.[jsonKeyMapping.FORM_GROUP] || [];
    if (!Array.isArray(group) || group.length === 0) return [];
    return group.filter(
      (fe: TemplateObject) => !isFormElementCompleted(fe, formData)
    );
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  // Snapshot the steps & missing fields when modal opens so typing/selecting
  // inside the modal doesn't cause the list to change under the user.
  const [snapshotStepsWithMissing, setSnapshotStepsWithMissing] = useState<
    TemplateObject[] | null
  >(null);
  const [snapshotMissingFieldsMap, setSnapshotMissingFieldsMap] = useState<
    Record<string, TemplateObject[]>
  >({});

  useEffect(() => {
    // Reset indexes when the modal opens or closes.
    // IMPORTANT: do NOT re-snapshot when the live list of missing steps changes
    // while the modal is open — that causes typing/selecting to move the
    // user's position unexpectedly. We only snapshot at the moment the modal
    // opens so the UI remains stable until it's closed.
    setCurrentStepIndex(0);
    setCurrentFieldIndex(0);
    if (open) {
      setSnapshotStepsWithMissing(stepsWithMissing);
      const map: Record<string, TemplateObject[]> = {};
      (stepsWithMissing || []).forEach((s) => {
        map[s[jsonKeyMapping.SLUG] || s[jsonKeyMapping.NAV_TITLE] || ""] =
          computeMissingFieldsForStep(s);
      });
      setSnapshotMissingFieldsMap(map);
      // If an initialStepSlug is provided, jump to that step index
      if (initialStepSlug) {
        const idx = (stepsWithMissing || []).findIndex(
          (s) => s[jsonKeyMapping.SLUGIFY] === initialStepSlug
        );
        if (idx >= 0) {
          setCurrentStepIndex(idx);
          setCurrentFieldIndex(0);
        }
      }
    } else {
      setSnapshotStepsWithMissing(null);
      setSnapshotMissingFieldsMap({});
    }
    // Intentionally only depend on `open` so the snapshot remains frozen
    // while the modal is visible.
  }, [open]);

  const eventFromSelectPortal = (e: React.MouseEvent) => {
    const path =
      (e as any).nativeEvent?.composedPath?.() ||
      (e as any).nativeEvent?.path ||
      [];
    for (const node of path) {
      try {
        if (!node || !node.className) continue;
        const cls = typeof node.className === "string" ? node.className : "";
        if (
          cls.indexOf("select__menu") !== -1 ||
          cls.indexOf("menuPortalTarget") !== -1
        )
          return true;
      } catch (err) {
        // ignore
      }
    }
    return false;
  };

  // Use the snapshot when available to keep the modal content stable while open.
  const effectiveSteps = snapshotStepsWithMissing || stepsWithMissing;
  const currentStep = effectiveSteps[currentStepIndex];
  const missingFields = ((): TemplateObject[] => {
    if (snapshotStepsWithMissing) {
      const key =
        currentStep?.[jsonKeyMapping.SLUG] ||
        currentStep?.[jsonKeyMapping.NAV_TITLE] ||
        "";
      return snapshotMissingFieldsMap[key] || [];
    }
    return computeMissingFieldsForStep(currentStep);
  })();
  const currentField = missingFields[currentFieldIndex]
    ? [missingFields[currentFieldIndex]]
    : [];

  const handleNext = () => {
    // Only advance when Next is pressed. Use snapshot length to avoid
    // recomputing while the modal is open.
    const totalFields = missingFields.length;
    if (currentFieldIndex < totalFields - 1) {
      setCurrentFieldIndex((p) => p + 1);
      return;
    }
    const totalSteps = (effectiveSteps || []).length;
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((p) => p + 1);
      setCurrentFieldIndex(0);
      return;
    }
    onClose();
  };

  const handlePrev = () => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex((p) => p - 1);
      return;
    }
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      // When a snapshot exists, use it to determine the previous step's
      // missing fields count. Otherwise fall back to computing from live data.
      if (snapshotStepsWithMissing) {
        const prevStep = snapshotStepsWithMissing[prevIndex];
        const key =
          prevStep?.[jsonKeyMapping.SLUG] ||
          prevStep?.[jsonKeyMapping.NAV_TITLE] ||
          "";
        const prevMissing = snapshotMissingFieldsMap[key] || [];
        setCurrentFieldIndex(Math.max(0, prevMissing.length - 1));
      } else {
        const prevMissing = computeMissingFieldsForStep(
          stepsWithMissing[prevIndex]
        );
        setCurrentFieldIndex(Math.max(0, prevMissing.length - 1));
      }
      return;
    }
  };

  const modalContent = open ? (
    <div
      className={`missing-info-backdrop`}
      role="presentation"
      data-open={open ? "true" : "false"}
      onClick={(e) => {
        if (eventFromSelectPortal(e as any)) return;
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={!open}
    >
      <div className="missing-info-modal" role="dialog" aria-modal="true">
        <div className="missing-info-modal-close-container">
          <button
            onClick={onClose}
            aria-label="Close"
            className="missing-info-modal-close"
          >
            <svg
              className="missing-info-modal-close-icon"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.27357 7.68153C7.597 7.35811 8.12137 7.35811 8.4448 7.68153L12.0001 11.2368L15.5554 7.68153C15.8788 7.35811 16.4032 7.35811 16.7266 7.68153C17.05 8.00496 17.05 8.52933 16.7266 8.85276L13.1713 12.408L16.7266 15.9633C17.05 16.2867 17.05 16.8111 16.7266 17.1345C16.4032 17.458 15.8788 17.458 15.5554 17.1345L12.0001 13.5793L8.4448 17.1345C8.12137 17.458 7.597 17.458 7.27357 17.1345C6.95015 16.8111 6.95015 16.2867 7.27357 15.9633L10.8289 12.408L7.27357 8.85276C6.95015 8.52933 6.95015 8.00496 7.27357 7.68153Z"
                fill="#A1A7B0"
              />
            </svg>
          </button>
        </div>

        <div className="missing-info-modal-header">
          <div className="missing-info-modal-header-inner">
            <div className="missing-info-modal-header-left">
              <span className="editing-label">Editing /&nbsp;</span>
              <span className="editing-slug">
                {currentStep?.[jsonKeyMapping.SLUG] ||
                  currentStep?.[jsonKeyMapping.NAV_TITLE] ||
                  "Step"}
              </span>
            </div>

            <div className="missing-info-modal-header-center">
              <span>Step&nbsp;</span>
              <span className="counter-primary">{currentFieldIndex + 1}</span>
              <span>&nbsp;of&nbsp;</span>
              <span className="counter-primary">{missingFields.length}</span>
            </div>
          </div>
        </div>

        {missingFields.length > 0 && currentField.length > 0 ? (
          <div
            className="missing-info-modal-alert"
            role="status"
            aria-live="polite"
          >
            <div>
              <div
                className="missing-info-modal-alert-icon"
                aria-hidden="true"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/static/images/text.svg`}
                  alt="alert icon"
                  width="8"
                  height="8"
                  onError={(e) => {
                    console.log('Image failed to load:', e);
                    console.log('Trying alternative path...');
                    // Try alternative path
                    (e.target as HTMLImageElement).src = '/static/images/Icon.png';
                  }}
                  onLoad={() => console.log('Image loaded successfully')}
                />
              </div>

              <div className="missing-info-modal-alert-inner">
                <div>
                  Complete the empty fields to avoid missing information.
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="missing-info-modal-body">
          <div className="missing-info-modal-title">
            <div className="missing-info-modal-title-text">
              {currentStep?.[jsonKeyMapping.SLUG] ||
                currentStep?.[jsonKeyMapping.DESCRIPTION] ||
                "Question"}
            </div>
            {currentStep?.[jsonKeyMapping.DESCRIPTION] ? (
              <div className="missing-info-modal-title-description" >
                {currentStep[jsonKeyMapping.DESCRIPTION]}
              </div>
            ) : null}
          </div>

          <div className="missing-info-modal-content">
            {currentField.length > 0 ? (
              <FormBuilder
                formGroup={currentField as any}
                stepDetails={currentStep}
                disableAutoAdvance={true}
              />
            ) : (
              <div style={{ color: "#7a7a7a" }}>
                No missing information found.
              </div>
            )}
          </div>
        </div>

        {/* Fixed action bar */}
        <div className="missing-info-modal-next-container">
          <button
            onClick={handleNext}
            className="missing-info-next-btn"
            aria-label={
              currentStepIndex === stepsWithMissing.length - 1 &&
              currentFieldIndex === missingFields.length - 1
                ? "Finish"
                : "Go to next missing field"
            }
          >
            <span className="missing-info-next-text">
              {currentStepIndex === stepsWithMissing.length - 1 &&
              currentFieldIndex === missingFields.length - 1
                ? "Done"
                : "Next"}
            </span>
            <span className="missing-info-next-icon-wrapper" aria-hidden="true">
              <svg
                className="missing-info-next-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12.3515 6.35147C12.8201 5.88284 13.5799 5.88284 14.0485 6.35147L18.8485 11.1515C19.3172 11.6201 19.3172 12.3799 18.8485 12.8485L14.0485 17.6485C13.5799 18.1172 12.8201 18.1172 12.3515 17.6485C11.8828 17.1799 11.8828 16.4201 12.3515 15.9515L15.1029 13.2L6 13.2C5.33726 13.2 4.8 12.6627 4.8 12C4.8 11.3373 5.33726 10.8 6 10.8H15.1029L12.3515 8.04853C11.8828 7.5799 11.8828 6.8201 12.3515 6.35147Z"
                  fill="white"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // Body class & scroll lock
  useEffect(() => {
    if (open) {
      document.body.classList.add('missing-info-modal-open');
    } else {
      document.body.classList.remove('missing-info-modal-open');
    }
    return () => { document.body.classList.remove('missing-info-modal-open'); };
  }, [open]);

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
};

export default MissingInfoModal;
